import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User, TrainingPlan, UserProfile, ProgressLog, NutritionPlan, SavedExercise } from "@/types";
import { authClient } from "@/lib/auth";
import { api } from "@/lib/api";

interface AuthContextType {
  user: User | null;
  plan: TrainingPlan | null;
  isLoading: boolean;
  saveProfile: (
    profile: Omit<UserProfile, "userId" | "updatedAt">,
  ) => Promise<void>;
  generatePlan: () => Promise<void>;
  refreshData: () => Promise<void>;
  // Progress
  progressLogs: ProgressLog[];
  refreshProgress: () => Promise<void>;
  // Nutrition
  nutritionPlan: NutritionPlan | null;
  refreshNutrition: () => Promise<void>;
  // Favorites
  favorites: SavedExercise[];
  refreshFavorites: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [neonUser, setNeonUser] = useState<any>(null);
  const [plan, setPlan] = useState<TrainingPlan | null>(null);
  const [progressLogs, setProgressLogs] = useState<ProgressLog[]>([]);
  const [nutritionPlan, setNutritionPlan] = useState<NutritionPlan | null>(null);
  const [favorites, setFavorites] = useState<SavedExercise[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    async function loadUser() {
      try {
        const result = await authClient.getSession();
        if (result && result.data?.user) {
          setNeonUser(result.data.user);
        } else {
          setNeonUser(null);
        }
      } catch (err) {
        setNeonUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    loadUser();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      if (neonUser?.id) {
        refreshData();
        refreshProgress();
        refreshNutrition();
        refreshFavorites();
      } else {
        setPlan(null);
        setProgressLogs([]);
        setNutritionPlan(null);
        setFavorites([]);
      }
      setIsLoading(false);
    }
  }, [neonUser?.id, isLoading]);

  const refreshData = useCallback(async () => {
    if (!neonUser || isRefreshingRef.current) return;

    isRefreshingRef.current = true;

    try {
      const planData = await api.getCurrentPlan(neonUser.id).catch(() => null);
      if (planData) {
        setPlan({
          id: planData.id,
          userId: planData.userId,
          overview: planData.planJson.overview,
          weeklySchedule: planData.planJson.weeklySchedule,
          progression: planData.planJson.progression,
          version: planData.version,
          createdAt: planData.createdAt,
        });
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [neonUser?.id]);

  const refreshProgress = useCallback(async () => {
    if (!neonUser) return;
    try {
      const logs = await api.getProgressHistory(neonUser.id).catch(() => []);
      setProgressLogs(logs);
    } catch (error) {
      console.error("Error refreshing progress:", error);
    }
  }, [neonUser?.id]);

  const refreshNutrition = useCallback(async () => {
    if (!neonUser) return;
    try {
      const plan = await api.getCurrentNutritionPlan(neonUser.id).catch(() => null);
      setNutritionPlan(plan);
    } catch (error) {
      console.error("Error refreshing nutrition:", error);
    }
  }, [neonUser?.id]);

  const refreshFavorites = useCallback(async () => {
    if (!neonUser) return;
    try {
      const favs = await api.getFavoriteExercises(neonUser.id).catch(() => []);
      setFavorites(favs);
    } catch (error) {
      console.error("Error refreshing favorites:", error);
    }
  }, [neonUser?.id]);

  async function saveProfile(
    profileData: Omit<UserProfile, "userId" | "updatedAt">,
  ) {
    if (!neonUser) {
      throw new Error("User must be authenticated to save profile");
    }

    await api.saveProfile(neonUser.id, profileData);
    await refreshData();
  }

  async function generatePlan() {
    if (!neonUser) {
      throw new Error("User must be authenticated to generate plan");
    }

    await api.generatePlan(neonUser.id);
    await refreshData();
  }

  return (
    <AuthContext.Provider
      value={{
        user: neonUser,
        plan,
        isLoading,
        saveProfile,
        generatePlan,
        refreshData,
        progressLogs,
        refreshProgress,
        nutritionPlan,
        refreshNutrition,
        favorites,
        refreshFavorites,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
